import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ResidentSubscription,
  getResidentSubscriptions,
  updateAllResidentSubscriptions,
  updateResidentSubscription,
} from 'src/service/engageFrontendGateway';
import { MOCK_RESIDENT_SUBSCRIPTIONS } from './mockPreferences';

export const PREFERENCES_QUERY_KEY = ['resident', 'subscriptions'] as const;

interface PreferencesContext {
  region: string;
  isMock?: boolean;
}

function buildQueryKey(region: string, isMock?: boolean) {
  return [...PREFERENCES_QUERY_KEY, isMock ? 'mock' : 'live', region] as const;
}

export function usePreferencesQuery({ region, isMock, enabled = true }: PreferencesContext & { enabled?: boolean }) {
  return useQuery<ResidentSubscription[]>({
    queryKey: buildQueryKey(region, isMock),
    queryFn: () => (isMock ? Promise.resolve(MOCK_RESIDENT_SUBSCRIPTIONS) : getResidentSubscriptions(region)),
    enabled,
  });
}

interface MutationVariables {
  subscriptionId: string;
  isEnabled: boolean;
}

interface MutationContext {
  previousIsEnabled?: boolean;
}

export function useUpdatePreferenceMutation({ region, isMock }: PreferencesContext) {
  const queryClient = useQueryClient();
  const queryKey = buildQueryKey(region, isMock);

  return useMutation<ResidentSubscription | null, Error, MutationVariables, MutationContext>({
    mutationFn: ({ subscriptionId, isEnabled }) => {
      if (isMock) return Promise.resolve(null);
      return updateResidentSubscription(subscriptionId, isEnabled, region);
    },
    onMutate: async ({ subscriptionId, isEnabled }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ResidentSubscription[]>(queryKey);
      const previousIsEnabled = previous?.find((entry) => entry.id === subscriptionId)?.isEnabled;
      queryClient.setQueryData<ResidentSubscription[]>(queryKey, (current) =>
        current?.map((entry) => (entry.id === subscriptionId ? { ...entry, isEnabled } : entry)),
      );
      return { previousIsEnabled };
    },
    // Roll back ONLY the failing subscription so concurrent mutations don't
    // trample each other's optimistic state.
    onError: (_error, { subscriptionId }, context) => {
      if (context?.previousIsEnabled === undefined) return;
      queryClient.setQueryData<ResidentSubscription[]>(queryKey, (current) =>
        current?.map((entry) =>
          entry.id === subscriptionId ? { ...entry, isEnabled: context.previousIsEnabled as boolean } : entry,
        ),
      );
    },
    onSettled: () => {
      if (isMock) return undefined;
      return queryClient.invalidateQueries({ queryKey });
    },
  });
}

interface BulkMutationVariables {
  isEnabled: boolean;
}

interface BulkMutationContext {
  previous?: ResidentSubscription[];
}

export function useUpdateAllPreferencesMutation({ region, isMock }: PreferencesContext) {
  const queryClient = useQueryClient();
  const queryKey = buildQueryKey(region, isMock);

  return useMutation<{ updated: number } | null, Error, BulkMutationVariables, BulkMutationContext>({
    mutationFn: ({ isEnabled }) => {
      if (isMock) return Promise.resolve(null);
      return updateAllResidentSubscriptions(isEnabled, region);
    },
    onMutate: async ({ isEnabled }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ResidentSubscription[]>(queryKey);
      queryClient.setQueryData<ResidentSubscription[]>(queryKey, (current) =>
        current?.map((entry) => ({ ...entry, isEnabled })),
      );
      return { previous };
    },
    // Whole-snapshot rollback is correct: there's only ever one in-flight bulk
    // mutation so it can't trample a concurrent per-row optimistic update.
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      if (isMock) return undefined;
      return queryClient.invalidateQueries({ queryKey });
    },
  });
}
